import React, { useState } from 'react';
import styled from 'styled-components';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { colors } from '../styles/GlobalStyles';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
`;

const Sidebar = styled.aside`
  width: ${props => props.collapsed ? '70px' : '250px'};
  background-color: ${colors.primary};
  color: white;
  transition: width 0.3s ease;
  position: fixed;
  height: 100vh;
  overflow-y: auto;
  z-index: 1000;
  left: 0;
  top: 0;

  @media (max-width: 768px) {
    width: 250px !important;
    left: ${props => props.mobileOpen ? '0' : '-250px'};
    transition: left 0.3s ease;
  }
`;

const SidebarHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Logo = styled.h1`
  font-size: 20px;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Nav = styled.nav`
  padding: 20px 0;
`;

const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  padding: 12px 20px;
  color: white;
  transition: all 0.3s ease;
  gap: 15px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  &.active {
    background-color: rgba(255, 255, 255, 0.2);
    border-left: 4px solid white;
  }
`;

const NavIcon = styled.span`
  font-size: 20px;
  min-width: 24px;
`;

const NavText = styled.span`
  font-size: 16px;
  white-space: nowrap;
`;

const MainContent = styled.main.attrs(props => ({
  sidebarCollapsed: undefined,
}))`
  flex: 1;
  margin-left: ${props => props.sidebarCollapsed ? '70px' : '250px'};
  background-color: ${colors.background};
  transition: margin-left 0.3s ease;

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const TopBar = styled.div`
  background-color: white;
  padding: 15px 30px;
  box-shadow: 0 2px 4px ${colors.shadow};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 24px;
  color: ${colors.primary};
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  position: relative;
  z-index: 101;
  outline: none;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  &:active {
    background-color: rgba(0, 0, 0, 0.1);
  }

  &:focus {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    display: block !important;
    min-width: 44px;
    min-height: 44px;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const LogoutButton = styled.button`
  background-color: ${colors.primary};
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.9;
  }
`;

const Content = styled.div`
  padding: 30px;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const MobileOverlay = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;

  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
  }
`;

export const AdminLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    toast.success('Çıkış yapıldı');
    navigate('/');
  };

  const handleMenuToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setMobileOpen(!mobileOpen);
  };

  return (
    <Container>
      <MobileOverlay $isOpen={mobileOpen} onClick={() => setMobileOpen(false)} />
      <Sidebar collapsed={collapsed} mobileOpen={mobileOpen}>
        <SidebarHeader>
          {!collapsed && <Logo>Yönetici Paneli</Logo>}
        </SidebarHeader>
        <Nav>
          <NavItem to="/admin/dashboard" onClick={() => setMobileOpen(false)}>
            <NavIcon>📊</NavIcon>
            {!collapsed && <NavText>Dashboard</NavText>}
          </NavItem>
          <NavItem to="/admin/members" onClick={() => setMobileOpen(false)}>
            <NavIcon>👥</NavIcon>
            {!collapsed && <NavText>Üyeler</NavText>}
          </NavItem>
          <NavItem to="/admin/classes" onClick={() => setMobileOpen(false)}>
            <NavIcon>🧘</NavIcon>
            {!collapsed && <NavText>Dersler</NavText>}
          </NavItem>
          <NavItem to="/admin/reservations" onClick={() => setMobileOpen(false)}>
            <NavIcon>📅</NavIcon>
            {!collapsed && <NavText>Rezervasyonlar</NavText>}
          </NavItem>
          <NavItem to="/admin/cancelled-reservations" onClick={() => setMobileOpen(false)}>
            <NavIcon>🚫</NavIcon>
            {!collapsed && <NavText>İptal Edilenler</NavText>}
          </NavItem>
          <NavItem to="/admin/class-occupancy" onClick={() => setMobileOpen(false)}>
            <NavIcon>📈</NavIcon>
            {!collapsed && <NavText>Ders Dolulukları</NavText>}
          </NavItem>
          <NavItem to="/admin/settings" onClick={() => setMobileOpen(false)}>
            <NavIcon>⚙️</NavIcon>
            {!collapsed && <NavText>Ayarlar</NavText>}
          </NavItem>
        </Nav>
      </Sidebar>

      <MainContent sidebarCollapsed={collapsed}>
        <TopBar>
          <MenuButton onClick={handleMenuToggle} aria-label="Menüyü Aç/Kapat">
            {mobileOpen ? '✕' : '☰'}
          </MenuButton>
          <UserInfo>
            <span>{user?.email}</span>
            <LogoutButton onClick={handleLogout}>Çıkış Yap</LogoutButton>
          </UserInfo>
        </TopBar>
        <Content>
          <Outlet />
        </Content>
      </MainContent>
    </Container>
  );
};

